import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  FileVideo,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { api, jsonBody } from './api'
import {
  uploadMediaFile,
  type MediaAsset,
  type UploadStatus,
} from './media-upload'
import { Progress } from './progress'
import type { Page } from './types'
import { EmptyState, PageHeader, StatusPill } from './ui'

interface UploadQueueItem {
  id: string
  file: File
  progress: number
  status: UploadStatus
  error?: string
}

const supportedTypes = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/quicktime',
])

const accept =
  'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,.jpg,.jpeg,.png,.webp,.mp4,.mov'

function queueId(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function queueStatus(item: UploadQueueItem) {
  switch (item.status) {
    case 'READY':
      return 'Ready to upload'
    case 'HASHING':
      return 'Calculating checksum'
    case 'UPLOADING':
      return 'Uploading to private storage'
    case 'PROCESSING':
      return 'Starting processing'
    case 'DONE':
      return 'Uploaded · processing queued'
    case 'ERROR':
      return 'Upload failed'
  }
}

export function MediaPage() {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [queue, setQueue] = useState<UploadQueueItem[]>([])
  const [dragging, setDragging] = useState(false)
  const [deletingAsset, setDeletingAsset] = useState<MediaAsset | null>(null)
  const [renamingAsset, setRenamingAsset] = useState<MediaAsset | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const media = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
    refetchInterval: (query) =>
      query.state.data?.items.some((item) =>
        ['QUEUED', 'PROCESSING'].includes(item.status)
      )
        ? 5000
        : false,
  })
  const upload = useMutation({
    mutationFn: async (items: UploadQueueItem[]) => {
      let completed = 0
      let failed = 0
      for (const item of items) {
        try {
          await uploadMediaFile(item.file, (progress, status) => {
            setQueue((current) =>
              current.map((candidate) =>
                candidate.id === item.id
                  ? { ...candidate, progress, status, error: undefined }
                  : candidate
              )
            )
          })
          completed += 1
        } catch (error) {
          failed += 1
          setQueue((current) =>
            current.map((candidate) =>
              candidate.id === item.id
                ? {
                    ...candidate,
                    status: 'ERROR',
                    error:
                      error instanceof Error
                        ? error.message
                        : 'The upload failed.',
                  }
                : candidate
            )
          )
        }
      }
      return { completed, failed }
    },
    onSuccess: async ({ completed, failed }) => {
      if (completed) {
        toast.success(
          `${completed} file${completed === 1 ? '' : 's'} uploaded. Processing has started.`
        )
      }
      if (failed) {
        toast.error(
          `${failed} file${failed === 1 ? '' : 's'} could not be uploaded.`
        )
      }
      await queryClient.invalidateQueries({ queryKey: ['media'] })
      if (inputRef.current) inputRef.current.value = ''
    },
  })

  const renameMedia = useMutation({
    mutationFn: ({ assetId, name }: { assetId: string; name: string }) =>
      api<MediaAsset>(`/media/${assetId}`, {
        method: 'PATCH',
        body: jsonBody({ name }),
      }),
    onSuccess: async (asset) => {
      setRenamingAsset(null)
      setRenameValue('')
      await queryClient.invalidateQueries({ queryKey: ['media'] })
      toast.success(`Media renamed to ${asset.name}.`)
    },
    onError: (error) => toast.error(error.message),
  })

  const removeMedia = useMutation({
    mutationFn: (assetId: string) =>
      api<void>(`/media/${assetId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      const name = deletingAsset?.name
      setDeletingAsset(null)
      await queryClient.invalidateQueries({ queryKey: ['media'] })
      toast.success(
        name ? `${name} deleted from the media library.` : 'Media deleted.'
      )
    },
    onError: (error) => toast.error(error.message),
  })
  const addFiles = (files: File[]) => {
    const accepted: UploadQueueItem[] = []
    const rejected: string[] = []
    for (const file of files) {
      if (!supportedTypes.has(file.type) || file.size <= 0) {
        rejected.push(file.name)
        continue
      }
      accepted.push({
        id: queueId(file),
        file,
        progress: 0,
        status: 'READY',
      })
    }
    if (rejected.length) {
      toast.error(
        `Unsupported or empty: ${rejected.slice(0, 3).join(', ')}${
          rejected.length > 3 ? '…' : ''
        }`
      )
    }
    if (accepted.length) {
      setQueue((current) => [...current, ...accepted].slice(0, 20))
    }
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    addFiles(Array.from(event.dataTransfer.files))
  }

  const pending = useMemo(
    () => queue.filter((item) => item.status === 'READY'),
    [queue]
  )
  const active = queue.some((item) =>
    ['HASHING', 'UPLOADING', 'PROCESSING'].includes(item.status)
  )
  const uploadPending = upload.isPending
  const uploadQueued = upload.mutate

  useEffect(() => {
    if (!uploadPending && pending.length) {
      uploadQueued(pending)
    }
  }, [pending, uploadPending, uploadQueued])

  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Content'
        title='Media library'
        description='Upload images and videos directly to private storage. Every file is signature-checked, scanned, probed, and converted into offline-ready screen variants.'
        action={
          <Button onClick={() => inputRef.current?.click()}>
            <Plus className='size-4' />
            Choose image or video
          </Button>
        }
      />

      <Input
        ref={inputRef}
        type='file'
        accept={accept}
        multiple
        className='sr-only'
        aria-label='Choose image or video files'
        onChange={(event) => {
          addFiles(Array.from(event.target.files ?? []))
          event.currentTarget.value = ''
        }}
      />

      <section className='rounded-2xl border bg-card p-4 sm:p-6'>
        <div
          className={`grid min-h-44 cursor-pointer place-items-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragging
              ? 'border-primary bg-primary/10'
              : 'border-muted-foreground/30 bg-muted/20 hover:border-primary/60 hover:bg-primary/5'
          }`}
          role='button'
          tabIndex={0}
          aria-label='Upload image or video drop zone'
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              inputRef.current?.click()
            }
          }}
          onDragEnter={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setDragging(false)
            }
          }}
          onDrop={handleDrop}
        >
          <div>
            <div className='mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-primary/10 text-primary'>
              <UploadCloud className='size-7' />
            </div>
            <h2 className='text-lg font-semibold'>
              Drop images or videos here
            </h2>
            <p className='mt-1 text-sm text-muted-foreground'>
              or click anywhere in this box to choose files
            </p>
            <div className='mt-4 flex flex-wrap justify-center gap-2 text-xs text-muted-foreground'>
              {['JPEG', 'PNG', 'WebP', 'MP4', 'MOV'].map((type) => (
                <span
                  key={type}
                  className='rounded-full border bg-background px-3 py-1'
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        </div>

        {queue.length ? (
          <div className='mt-5 space-y-3'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <div>
                <h3 className='font-semibold'>Upload queue</h3>
                <p className='text-xs text-muted-foreground'>
                  Upload starts automatically after selection. Up to 20 files
                  can be queued at once.
                </p>
              </div>
              <div className='flex flex-wrap gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={active}
                  onClick={() =>
                    setQueue((current) =>
                      current.filter((item) => item.status !== 'DONE')
                    )
                  }
                >
                  Clear completed
                </Button>
                {pending.length || upload.isPending ? (
                  <Button
                    type='button'
                    size='sm'
                    disabled={upload.isPending}
                    onClick={() => upload.mutate(pending)}
                  >
                    {upload.isPending ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      <UploadCloud className='size-4' />
                    )}
                    {upload.isPending
                      ? 'Uploading…'
                      : `Upload now (${pending.length})`}
                  </Button>
                ) : (
                  <span className='rounded-full border bg-background px-3 py-1 text-xs text-emerald-600'>
                    Automatic upload on
                  </span>
                )}
              </div>
            </div>

            <div className='grid gap-3 md:grid-cols-2'>
              {queue.map((item) => {
                const isVideo = item.file.type.startsWith('video/')
                return (
                  <article
                    key={item.id}
                    className='rounded-xl border bg-background p-3'
                  >
                    <div className='flex items-start gap-3'>
                      <div className='grid size-10 shrink-0 place-items-center rounded-lg bg-muted'>
                        {isVideo ? (
                          <FileVideo className='size-5 text-primary' />
                        ) : (
                          <ImageIcon className='size-5 text-primary' />
                        )}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='min-w-0'>
                            <p
                              className='truncate text-sm font-medium'
                              title={item.file.name}
                            >
                              {item.file.name}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                              {formatBytes(item.file.size)} ·{' '}
                              {isVideo ? 'Video' : 'Image'}
                            </p>
                          </div>
                          {item.status === 'DONE' ? (
                            <CheckCircle2 className='size-5 shrink-0 text-emerald-500' />
                          ) : (
                            <Button
                              type='button'
                              size='icon'
                              variant='ghost'
                              className='size-8'
                              disabled={
                                !['READY', 'ERROR'].includes(item.status)
                              }
                              onClick={() =>
                                setQueue((current) =>
                                  current.filter(
                                    (candidate) => candidate.id !== item.id
                                  )
                                )
                              }
                              aria-label={`Remove ${item.file.name}`}
                            >
                              <Trash2 className='size-4' />
                            </Button>
                          )}
                        </div>
                        <div className='mt-2'>
                          <div className='mb-1 flex justify-between text-[11px] text-muted-foreground'>
                            <span>{queueStatus(item)}</span>
                            <span>{item.progress}%</span>
                          </div>
                          <Progress value={item.progress} />
                          {item.error ? (
                            <p className='mt-1 text-xs text-red-500'>
                              {item.error}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          </div>
        ) : null}
      </section>

      {media.data?.items.length ? (
        <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
          {media.data.items.map((asset) => (
            <article
              key={asset.id}
              className='group overflow-hidden rounded-xl border bg-card'
            >
              <div className='relative grid aspect-video place-items-center overflow-hidden bg-zinc-950'>
                {asset.thumbnailUrl ? (
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.name}
                    className='size-full object-cover transition-transform group-hover:scale-[1.03]'
                  />
                ) : asset.kind === 'VIDEO' ? (
                  <FileVideo className='size-10 text-zinc-700' />
                ) : (
                  <ImageIcon className='size-10 text-zinc-700' />
                )}
                <span className='absolute end-3 top-3'>
                  <StatusPill value={asset.status} />
                </span>
              </div>
              <div className='p-4'>
                <div className='flex items-start justify-between gap-2'>
                  <p
                    className='min-w-0 truncate font-semibold'
                    title={asset.name}
                  >
                    {asset.name}
                  </p>
                  <div className='-me-2 -mt-2 flex shrink-0 items-center'>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='size-8'
                      aria-label={`Rename ${asset.name}`}
                      onClick={() => {
                        setRenamingAsset(asset)
                        setRenameValue(asset.name)
                      }}
                    >
                      <Pencil className='size-4' />
                    </Button>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='size-8 text-destructive hover:bg-destructive/10 hover:text-destructive'
                      aria-label={`Delete ${asset.name}`}
                      onClick={() => setDeletingAsset(asset)}
                    >
                      <Trash2 className='size-4' />
                    </Button>
                  </div>
                </div>
                <div className='mt-2 flex items-center justify-between text-xs text-muted-foreground'>
                  <span>{formatBytes(asset.sizeBytes)}</span>
                  <span>
                    {asset.width && asset.height
                      ? `${asset.width}×${asset.height}`
                      : asset.kind}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title='Your library is empty'
          description='Choose an image or video above. It becomes publishable after validation and processing.'
          action={
            <Button onClick={() => inputRef.current?.click()}>
              Choose image or video
            </Button>
          }
        />
      )}
      <Dialog
        open={Boolean(renamingAsset)}
        onOpenChange={(open) => {
          if (!open && !renameMedia.isPending) {
            setRenamingAsset(null)
            setRenameValue('')
          }
        }}
      >
        <DialogContent>
          <form
            className='space-y-5'
            onSubmit={(event) => {
              event.preventDefault()
              const name = renameValue.trim()
              if (renamingAsset && name) {
                renameMedia.mutate({ assetId: renamingAsset.id, name })
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename media</DialogTitle>
              <DialogDescription>
                Change the library display name. The stored file and published
                content remain unchanged.
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-2'>
              <label htmlFor='media-name' className='text-sm font-medium'>
                Media name
              </label>
              <Input
                id='media-name'
                value={renameValue}
                maxLength={255}
                autoFocus
                onChange={(event) => setRenameValue(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                disabled={renameMedia.isPending}
                onClick={() => {
                  setRenamingAsset(null)
                  setRenameValue('')
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={
                  renameMedia.isPending ||
                  !renameValue.trim() ||
                  renameValue.trim() === renamingAsset?.name
                }
              >
                {renameMedia.isPending ? (
                  <Loader2 className='size-4 animate-spin' />
                ) : null}
                {renameMedia.isPending ? 'Saving…' : 'Save name'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={Boolean(deletingAsset)}
        onOpenChange={(open) => {
          if (!open && !removeMedia.isPending) setDeletingAsset(null)
        }}
        title='Delete media?'
        desc={
          deletingAsset
            ? `“${deletingAsset.name}” will disappear from the media library. Media used by an active slide or playlist cannot be deleted.`
            : 'This media will disappear from the library.'
        }
        confirmText={removeMedia.isPending ? 'Deleting…' : 'Delete media'}
        destructive
        isLoading={removeMedia.isPending}
        handleConfirm={() => {
          if (deletingAsset) removeMedia.mutate(deletingAsset.id)
        }}
      />
    </div>
  )
}
